import { useDistricts, useProvinces } from "hooks";
import { useEffect, useMemo, useState } from "react";

const initLocalitiesParams = {
  countryId: 1,
  type: 1,
  parent_code: "",
  record_status: "O",
};
export const mapOptionObjectToArray = object => {
  if (!object) return [];
  const objectValue = object && Object.keys(object);

  return objectValue?.map(item => ({
    label: object?.[item],
    value: item,
  }));
};

const useLocationData = ({
  mainForm,
  isAddressLevel2 = true,
  orderDetail,
  reloadKey,
}) => {
  const [provinceParams, setProvinceParams] = useState(initLocalitiesParams);
  const [districtParams, setDistrictParams] = useState(initLocalitiesParams);
  const [wardsParams, setWardsParams] = useState(initLocalitiesParams);

  const { setValue, getValues, watch } = mainForm;

  const { province_code, district_code } = watch();

  // Update record_status based on address level
  useEffect(() => {
    const recordStatus = isAddressLevel2 ? "O" : "C";
    setProvinceParams(prev => ({ ...prev, record_status: recordStatus }));
    setDistrictParams(prev => ({ ...prev, record_status: recordStatus }));
    setWardsParams(prev => ({ ...prev, record_status: recordStatus }));
  }, [isAddressLevel2]);

  // PROVINCE SELECT HANDLE
  const { data: provinces, refetch: refetchProvinces } = useProvinces(
    provinceParams,
    null,
    null,
    true
  );

  useEffect(() => {
    if (!reloadKey) return;
    refetchProvinces();
    if (orderDetail?.province_code) {
      if (isAddressLevel2) {
        setWardsParams(prev => ({
          ...prev,
          type: 2,
          parent_code: String(orderDetail.province_code),
          record_status: "O",
        }));
      } else {
        setDistrictParams(prev => ({
          ...prev,
          type: 2,
          parent_code: String(orderDetail.province_code),
          record_status: "C",
        }));
      }
    }
  }, [
    reloadKey,
    refetchProvinces,
    orderDetail?.province_code,
    isAddressLevel2,
  ]);

  const provinceOptions = useMemo(() => {
    return mapOptionObjectToArray(provinces);
  }, [provinces]);

  useEffect(() => {
    if (provinces && orderDetail?.province_code && provinceOptions.length > 0) {
      const targetProvince = String(orderDetail.province_code);
      const provinceOption = provinceOptions.find(
        p => String(p.value) === targetProvince
      );
      if (provinceOption) {
        setValue("province_code", provinceOption);
        if (isAddressLevel2) {
          setWardsParams(prev => ({
            ...prev,
            type: 2,
            parent_code: targetProvince,
            record_status: "O",
          }));
        } else {
          setDistrictParams(prev => ({
            ...prev,
            type: 2,
            parent_code: targetProvince,
            record_status: "C",
          }));
        }
      }
    }
  }, [
    provinces,
    orderDetail?.province_code,
    provinceOptions,
    setValue,
    isAddressLevel2,
  ]);
  const onChangeProvince = item => {
    setValue("ward_code", null);
    setValue("province_code", item);

    if (isAddressLevel2) {
      setWardsParams(prev => ({
        ...prev,
        type: 2,
        parent_code: String(item.value),
        record_status: "O",
      }));
    } else {
      setValue("district_code", null);
      setDistrictParams(prev => ({
        ...prev,
        type: 2,
        parent_code: String(item.value),
        record_status: "C",
      }));
    }
  };

  const { data: districts, refetch: refetchDistricts } = useDistricts(
    districtParams,
    null,
    null,
    true
  );

  useEffect(() => {
    if (districtParams?.type === 2 && districtParams?.parent_code)
      refetchDistricts();
  }, [districtParams, refetchDistricts]);

  const districtOptions = useMemo(() => {
    return mapOptionObjectToArray(districts);
  }, [districts]);

  useEffect(() => {
    if (districts && orderDetail?.district_code && districtOptions.length > 0) {
      const targetDistrict = String(orderDetail.district_code);
      const districtOption = districtOptions.find(
        d => String(d.value) === targetDistrict
      );
      if (districtOption) {
        setValue("district_code", districtOption);
        setWardsParams(prev => ({
          ...prev,
          type: 3,
          parent_code: targetDistrict,
          record_status: "C",
        }));
      }
    }
  }, [districts, orderDetail?.district_code, districtOptions, setValue]);

  const onChangeDistrict = item => {
    setValue("ward_code", null);
    setValue("district_code", item);
    setWardsParams(prev => ({
      ...prev,
      type: 3,
      parent_code: item.value,
      record_status: "C",
    }));
  };

  const isDisableDistrictSelect = useMemo(() => {
    return !Boolean(province_code);
  }, [province_code]);

  // WARD SELECT HANDLE
  // WARD SELECT HANDLE
  const { data: wards, refetch: refetchWards } = useDistricts(
    wardsParams,
    null,
    null,
    true
  );
  const wardOptions = useMemo(() => mapOptionObjectToArray(wards), [wards]);

  // ADD: refetch wards when params ready
  useEffect(() => {
    if (
      (wardsParams?.type === 2 || wardsParams?.type === 3) &&
      wardsParams?.parent_code
    ) {
      refetchWards();
    }
  }, [wardsParams, refetchWards]);
  useEffect(() => {
    if (wards && wardOptions.length > 0) {
      const targetWard = isAddressLevel2
        ? orderDetail?.ward_code ||
          orderDetail?.district_code ||
          orderDetail?.customer_district_code
        : orderDetail?.ward_code;

      if (targetWard) {
        const wardOption =
          wardOptions.find(w => String(w.value) === String(targetWard)) ||
          (isAddressLevel2
            ? wardOptions.find(w => {
                const v = String(w.value).toLowerCase();
                const l = String(w.label).toLowerCase();
                const t = String(targetWard).toLowerCase();
                return v === t || l.includes(t) || t.includes(l);
              })
            : null);

        if (wardOption) {
          setValue("ward_code", wardOption);
        }
      }
    }
  }, [
    wards,
    wardOptions,
    orderDetail?.ward_code,
    orderDetail?.district_code,
    orderDetail?.customer_district_code,
    isAddressLevel2,
    setValue,
  ]);

  const onChangeWard = item => {
    setValue("ward_code", item);
  };

  const isDisableWardSelect = useMemo(() => {
    if (isAddressLevel2) {
      return !Boolean(province_code);
    } else {
      return !Boolean(district_code);
    }
  }, [province_code, district_code, isAddressLevel2]);

  return {
    provinceOptions,
    onChangeProvince,
    districtOptions: districtOptions,
    onChangeDistrict,
    isDisableDistrictSelect,
    wardOptions,
    onChangeWard,
    isDisableWardSelect,
  };
};

export default useLocationData;
